import * as React from "react"
import { Loader2, Paperclip, Receipt, Send } from "lucide-react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { useBookingT } from "@/hooks/use-translations"
import { formatUserDate, formatUserDateTime } from "@/lib/date-time"
import { formatMoneyAmount } from "@/lib/money"
import {
  cachedRead,
  fetchExpenses,
  runExpenses,
  updateConnection,
  type HrisConnection,
  type HrisExpenseKind,
  type HrisExpenses,
} from "@/features/integrations/hris/hrisApi"

type Props = {
  connection: HrisConnection
  /** Bumped by the parent when the legal state changes, to reload availability. */
  refreshKey: number
}

const NONE = "__none__"

/** The saved mapping, or the suggestion by name until one is saved. */
function initialMap(data: Extract<HrisExpenses, { supported: true }>): Partial<Record<HrisExpenseKind, string>> {
  return Object.keys(data.map).length > 0 ? data.map : data.suggested
}

/**
 * Schedule E §2.2: approved expenses written to the HR system's expense module
 * (Cleemy for Lucca). Off by default and switched on here, with the fields it
 * writes listed first; only expenses approved after that go out. The customer
 * picks one of their own categories for each kind of expense.
 */
export function HrisExpensesCard({ connection, refreshKey }: Props) {
  const t = useBookingT()
  const [state, setState] = React.useState<HrisExpenses | null>(() => cachedRead<HrisExpenses>(`expenses:${connection.id}`) ?? null)
  const [map, setMap] = React.useState<Partial<Record<HrisExpenseKind, string>>>(() => (state?.supported ? initialMap(state) : {}))
  const [confirm, setConfirm] = React.useState<"on" | "off" | null>(null)
  const [busy, setBusy] = React.useState(false)

  const load = React.useCallback(() => {
    fetchExpenses(connection.id)
      .then((data) => {
        setState(data)
        if (data.supported) {
          setMap(initialMap(data))
        }
      })
      .catch(() => setState(null))
  }, [connection.id])

  React.useEffect(load, [load, refreshKey])

  if (!state || !state.supported) return null

  const provider = connection.providerName
  const kindLabel: Record<HrisExpenseKind, string> = {
    flight: t("hris_exp_kind_flight", "Flights"),
    hotel: t("hris_exp_kind_hotel", "Hotels"),
    train: t("hris_exp_kind_train", "Trains"),
    car: t("hris_exp_kind_car", "Car rental"),
    taxi: t("hris_exp_kind_taxi", "Taxi and transfers"),
    meals: t("hris_exp_kind_meals", "Meals"),
    other: t("hris_exp_kind_other", "Everything else"),
  }
  const statusBadge = {
    sent: <Badge variant="green">{t("hris_exp_sent", "Sent")}</Badge>,
    failed: <Badge variant="destructive">{t("hris_exp_failed", "Not sent")}</Badge>,
    skipped: <Badge variant="warning">{t("hris_exp_skipped", "Waiting for a category")}</Badge>,
  }
  const dirty = JSON.stringify(map) !== JSON.stringify(state.map)

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true)
    try {
      await fn()
      toast.success(done)
      setConfirm(null)
      load()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const saveMap = () =>
    act(() => updateConnection(connection.id, { settings: { expenseCategoryMap: map } }), t("hris_exp_map_saved", "Categories saved."))

  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <div className="flex items-start justify-between gap-3 max-md:flex-col">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Receipt className="size-5 text-brand-500" />
              <h2 className="text-lg font-semibold text-neutral-900">
                {t("hris_exp_title", "Send expenses to {provider}").replace("{provider}", provider)}
              </h2>
              {state.enabled ? (
                <Badge variant="green">{t("hris_offers_on", "On")}</Badge>
              ) : (
                <Badge variant="frozen">{t("hris_offers_off", "Off")}</Badge>
              )}
            </div>
            <p className="max-w-2xl text-sm text-neutral-800">
              {t(
                "hris_exp_intro",
                "Approved expenses of your employees appear in their {provider} expenses, ready to go into an expense claim. Only expenses approved after you switch this on are sent, each once.",
              ).replace("{provider}", provider)}
            </p>
            {state.enabled && state.since && (
              <p className="text-sm text-neutral-800">
                {t("hris_exp_since", "Sending expenses approved since {date}").replace("{date}", formatUserDateTime(state.since))}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {state.enabled && (
              <Button variant="outline" disabled={busy} onClick={() => void act(() => runExpenses(connection.id), t("hris_exp_run_queued", "Sending. The list updates in a minute."))}>
                <Send className="size-4" />
                {t("hris_exp_run", "Send now")}
              </Button>
            )}
            {state.enabled ? (
              <Button variant="outline" className="text-error-700" onClick={() => setConfirm("off")}>
                {t("hris_offers_disable", "Switch off")}
              </Button>
            ) : (
              <Button disabled={!state.available} onClick={() => setConfirm("on")}>
                {t("hris_offers_enable", "Switch on")}
              </Button>
            )}
          </div>
        </div>

        {!state.available && (
          <p className="text-sm text-neutral-800">
            {t("hris_offers_needs_legal", "Available once Schedule E and the permission to receive employee data are in force.")}
          </p>
        )}

        <div className="space-y-3 border-t border-neutral-200 pt-4">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">{t("hris_exp_categories", "Expense categories")}</h3>
            <p className="text-sm text-neutral-800">
              {t(
                "hris_exp_categories_hint",
                "Choose the {provider} category for each kind of expense. The list shows the categories your {provider} expenses already use; an employee whose expense profile does not allow a category gets the expense refused with the reason below.",
              ).split("{provider}").join(provider)}
            </p>
          </div>
          {state.categoriesError && <p className="text-sm text-error-700">{state.categoriesError}</p>}
          <div className="divide-y divide-neutral-200 rounded-md border border-neutral-200">
            {state.kinds.map((kind) => (
              <div key={kind} className="flex items-center justify-between gap-4 px-4 py-2.5 max-md:flex-col max-md:items-stretch">
                <span className="text-sm text-neutral-900">{kindLabel[kind]}</span>
                <Select value={map[kind] ?? NONE} onValueChange={(v) => setMap((m) => ({ ...m, [kind]: v === NONE ? undefined : v }))}>
                  <SelectTrigger className="w-72 max-md:w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>
                      {kind === "other" ? t("hris_exp_not_sent", "Do not send") : t("hris_exp_as_other", "As “Everything else”")}
                    </SelectItem>
                    {state.categories.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button onClick={() => void saveMap()} disabled={busy || !dirty}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t("hris_save", "Save")}
            </Button>
          </div>
        </div>

        {state.recent.length > 0 && (
          <div className="space-y-2 border-t border-neutral-200 pt-4">
            <h3 className="text-sm font-semibold text-neutral-900">
              {t("hris_exp_recent", "Latest expenses")}
              <span className="ml-2 font-normal text-neutral-800">
                {t("hris_exp_counts", "Sent: {sent} · Not sent: {failed} · Waiting: {skipped}")
                  .replace("{sent}", String(state.counts.sent ?? 0))
                  .replace("{failed}", String(state.counts.failed ?? 0))
                  .replace("{skipped}", String(state.counts.skipped ?? 0))}
              </span>
            </h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("hris_exp_col_date", "Date")}</TableHead>
                  <TableHead>{t("hris_exp_col_merchant", "Merchant")}</TableHead>
                  <TableHead className="text-right">{t("hris_exp_col_amount", "Amount")}</TableHead>
                  <TableHead>{t("hris_status", "Status")}</TableHead>
                  <TableHead>{t("hris_exp_col_note", "Details")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.recent.map((row) => (
                  <TableRow key={row.expenseId}>
                    <TableCell className="whitespace-nowrap text-sm">{row.date ? formatUserDate(row.date) : "—"}</TableCell>
                    <TableCell className="text-sm">{row.merchant ?? "—"}</TableCell>
                    <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                      {row.amount !== null && row.currency ? formatMoneyAmount(row.amount, row.currency) : "—"}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5">
                        {statusBadge[row.status]}
                        {row.receipt && <Paperclip className="size-3.5 text-neutral-800" aria-label={t("hris_exp_receipt", "Receipt attached")} />}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-neutral-800">{row.note ?? ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader className="text-left">
            <DialogTitle>
              {confirm === "on"
                ? t("hris_exp_on_title", "Send expenses to {provider}?").replace("{provider}", provider)
                : t("hris_exp_off_title", "Stop sending expenses?")}
            </DialogTitle>
            <DialogDescription className="text-neutral-800">
              {confirm === "on"
                ? t(
                    "hris_exp_on_text",
                    "For each approved expense, Travel Code writes to {provider}: the employee, the date, merchant, amount and currency, the category, the cost center and the receipt. What is written is then governed by your agreement with {provider}. You can switch this off at any time.",
                  ).split("{provider}").join(provider)
                : t("hris_exp_off_text", "Nothing more is sent. Expenses already in {provider} stay there.").replace("{provider}", provider)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>
              {t("hris_cancel", "Cancel")}
            </Button>
            <Button
              variant={confirm === "off" ? "destructive" : "default"}
              disabled={busy}
              onClick={() =>
                void act(
                  () => updateConnection(connection.id, { settings: { expenseWriteBack: confirm === "on" } }),
                  confirm === "on" ? t("hris_exp_on_done", "Switched on.") : t("hris_exp_off_done", "Switched off."),
                )
              }
            >
              {busy && <Loader2 className="size-4 animate-spin" />}
              {confirm === "on" ? t("hris_offers_enable", "Switch on") : t("hris_offers_disable", "Switch off")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
